'use client';

import { Pill } from "@/ui/controls/buttons";
import { FlexRow } from "@/ui/controls/layout";
import clsx from "clsx";
import { Suspense, useEffect, useState } from "react";
import { SERVICES } from "@/fe/services";
import { UsersSelector } from "./users";
import { NavLinks } from "./navigation";
import { useSearchParams } from "next/navigation";
import { TestingTagsSelector } from "./testing-tags";
import { CLM } from "@/common/schema/claims";
import assert from "assert";

export function RightHud({ BOOM_ENV, rawClaim, userId, userTestingTags, rawUsers }: {
  BOOM_ENV:         string,
  rawClaim?:        CLM.RawClaim,
  userId?:          string,
  userTestingTags?: string[],
  rawUsers:         CLM.RawUser[],
}) {
  const searchParams = useSearchParams();
  const ts = searchParams.get('ts') || "NOW";

  const [pinned,                       setPinned                      ] = useState(false);
  const [hide,                         setHide                        ] = useState(true );
  const [showCurrentUser,              setShowCurrentUser             ] = useState(false);
  const [showUserSettings,             setShowUserSettings            ] = useState(false);
  const [showUserExperimentalSettings, setShowUserExperimentalSettings] = useState(false);

  const [currentUserData,              setCurrentUserData             ] = useState(undefined as any | undefined);
  const [userSettingsData,             setUserSettingsData            ] = useState(undefined as any | undefined);
  const [userExperimentalSettingsData, setUserExperimentalSettingsData] = useState(undefined as any | undefined);

  const { DATA, CTX } = SERVICES();

  const { superUser } = CTX.useLoggedInContext();

  assert(superUser !== undefined, "currentUser is undefined");

  const claim = rawClaim ? DATA.fromRawClaim(rawClaim) : undefined;

  useEffect(() => {
    if (showCurrentUser) {
      setCurrentUserData({ ...superUser, settings: undefined, experimentalSettings: undefined });
    }
    if (showUserSettings) {
      setUserSettingsData(superUser.settings);
    }
    if (showUserExperimentalSettings) {
      setUserExperimentalSettingsData(superUser.experimentalSettings);
    }
  }, [showCurrentUser, showUserSettings, showUserExperimentalSettings, ts, superUser]);

  const showJson = showCurrentUser || showUserSettings || showUserExperimentalSettings;

  return <div className={clsx(
    "fixed z-50 right-4 bottom-2 max-w-sm overflow-auto bg-orange-50 p-2 border border-orange-200 rounded-md",
    !hide && pinned ? "opacity-100" : "opacity-0 hover:opacity-100"
  )}>

    {!hide && <Suspense>
      <UsersSelector       rawUsers={rawUsers} />
      <TestingTagsSelector BOOM_ENV={BOOM_ENV} claim={claim} userId={userId} userTestingTags={userTestingTags} />
      <NavLinks />
    </Suspense>}

    {!hide && showJson && <pre><code>
      {JSON.stringify({
        user:         showCurrentUser              ? currentUserData              : undefined,
        settings:     showUserSettings             ? userSettingsData             : undefined,
        experimental: showUserExperimentalSettings ? userExperimentalSettingsData : undefined,
      }, null, 2)}
    </code></pre>}

    <FlexRow className='place-content-end'>
      {!hide && <>
        <Pill className={clsx(showCurrentUser              && 'bg-blue-200 hover:bg-blue-300')} onClick={() => setShowCurrentUser             (!showCurrentUser             )}>U</Pill>
        <Pill className={clsx(showUserSettings             && 'bg-blue-200 hover:bg-blue-300')} onClick={() => setShowUserSettings            (!showUserSettings            )}>S</Pill>
        <Pill className={clsx(showUserExperimentalSettings && 'bg-blue-200 hover:bg-blue-300')} onClick={() => setShowUserExperimentalSettings(!showUserExperimentalSettings)}>EXP</Pill>
        <Pill className={clsx(pinned                       && 'bg-blue-200 hover:bg-blue-300')} onClick={() => setPinned                      (!pinned                      )}>{pinned ? 'Unpin' : 'Pin'}</Pill>
      </>}
      <Pill onClick={() => setHide(!hide)}>{hide ? 'Show' : 'Hide'}</Pill>
    </FlexRow>
  </div>;
}
